/**
 * Minimal, dependency-free Markdown renderer — enough for the static legal
 * drafts (headings, paragraphs, bold, inline code, ordered/unordered lists,
 * blockquotes, horizontal rules). Not a general-purpose Markdown engine; we keep
 * the skeleton lean and only support what the legal documents use.
 *
 * Input is our own bundled markdown (not user content), so this is not an XSS
 * surface; we still render text as React children (never dangerouslySetInnerHTML).
 */
import { Fragment } from 'react';

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Tokenize **bold** and `code`; everything else is plain text.
  const regex = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={`${keyPrefix}-t${i}`}>{text.slice(lastIndex, match.index)}</Fragment>);
    }
    if (match[2] != null) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{match[2]}</strong>);
    } else if (match[3] != null) {
      nodes.push(
        <code key={`${keyPrefix}-c${i}`} className="mono">
          {match[3]}
        </code>,
      );
    }
    lastIndex = regex.lastIndex;
    i += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`${keyPrefix}-t${i}`}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

export function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }
    if (/^---+\s*$/.test(line)) {
      blocks.push(<div className="divider" key={key++} style={{ margin: '16px 0' }} />);
      i += 1;
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const cls = level <= 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
      blocks.push(
        <div className={cls} key={key++} style={{ marginTop: 18 }}>
          {renderInline(heading[2], `h${key}`)}
        </div>,
      );
      i += 1;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^>\s?/, ''));
        i += 1;
      }
      blocks.push(
        <div
          key={key++}
          className="panel"
          style={{ padding: '12px 14px', borderColor: 'var(--amber)', background: 'var(--amber-soft)' }}
        >
          {renderInline(quote.join(' '), `q${key}`)}
        </div>,
      );
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={key++} style={{ paddingLeft: 22, margin: '4px 0', lineHeight: 1.6 }}>
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it, `ol${key}-${idx}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={key++} style={{ paddingLeft: 22, margin: '4px 0', lineHeight: 1.6 }}>
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it, `ul${key}-${idx}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    // Paragraph (consume consecutive non-blank, non-structural lines).
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6}\s|>\s?|---+\s*$|\d+\.\s|[-*]\s)/.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++} style={{ margin: '8px 0', lineHeight: 1.6 }}>
        {renderInline(para.join(' '), `p${key}`)}
      </p>,
    );
  }

  return <>{blocks}</>;
}
